import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Search, Ship, Users } from "lucide-react";
import { api } from "@/lib/api";
import { rp, dEN, BOOKING_STATUS, PAY_STATUS } from "@/lib/format";
import { PageHeader, Pill, Card, Empty, BigButton } from "@/components/admin/common";
import BookingForm from "@/components/admin/BookingForm";
import { Input } from "@/components/ui/input";

const TABS = [["upcoming", "Upcoming"], ["completed", "Completed"], ["cancelled", "Cancelled"], ["", "All"]];

export default function Bookings() {
  const navigate = useNavigate();
  const [status, setStatus] = useState("upcoming");
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [boats, setBoats] = useState({});
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/admin/bookings", { params: { status, q } }).then((r) => setItems(r.data)), [status, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/boats").then((r) => setBoats(Object.fromEntries(r.data.map((b) => [b.id, b.name])))); }, []);

  return (
    <div className="max-w-6xl" data-testid="bookings-page">
      <PageHeader title="Bookings" subtitle="Every trip, its boat and who has paid."
        actions={<BigButton onClick={() => setOpen(true)} data-testid="new-booking-btn"><Plus className="w-5 h-5" />New booking</BigButton>} />
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex bg-white border border-border rounded-xl p-1 overflow-x-auto">
          {TABS.map(([k, l]) => (
            <button key={l} onClick={() => setStatus(k)} data-testid={`bookings-tab-${l.toLowerCase()}`}
              className={`px-4 h-10 rounded-lg text-sm font-semibold whitespace-nowrap ${status === k ? "bg-ocean text-white" : "text-slate-600"}`}>{l}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or code" className="h-12 pl-9" data-testid="bookings-search-input" />
        </div>
      </div>
      {items && !items.length && <Empty text="No bookings here yet." testid="bookings-empty" />}
      <div className="grid md:grid-cols-2 gap-3">
        {items?.map((b) => (
          <Link key={b.id} to={`/admin/bookings/${b.id}`} data-testid={`booking-card-${b.id}`}>
            <Card className="p-4 hover:border-ocean transition-colors duration-150 h-full">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-extrabold text-[17px]">{b.customer_name}</p>
                  <p className="text-sm text-slate-500">{b.code} · {b.trip_type}</p>
                </div>
                <Pill map={BOOKING_STATUS} value={b.status} />
              </div>
              <p className="mt-3 font-semibold">{dEN(b.start_date)} – {dEN(b.end_date)}</p>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-slate-600">
                <span className="flex items-center gap-1"><Ship className="w-4 h-4" />{boats[b.boat_id] || "No boat"}</span>
                <span className="flex items-center gap-1"><Users className="w-4 h-4" />{b.guest_count}/{b.pax} guests</span>
                <span>{rp(b.price)}</span>
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <span className="flex items-center gap-1 text-slate-500">Guest <Pill map={PAY_STATUS} value={b.guest_pay_status} /></span>
                <span className="flex items-center gap-1 text-slate-500">Boat <Pill map={PAY_STATUS} value={b.vendor_pay_status} /></span>
              </div>
            </Card>
          </Link>
        ))}
      </div>
      <BookingForm open={open} onClose={() => setOpen(false)} onSaved={(b) => navigate(`/admin/bookings/${b.id}`)} />
    </div>
  );
}
